import { AxiosResponse } from 'axios';
import { milliseconds } from 'date-fns';
import { cloneDeep, sortBy } from 'lodash';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import V2Error from '@/app/V2Error';
import {
  ServiceDto,
  ServiceWrapperDto,
  ServicesDto,
  VenueServiceModel,
  useVenueServiceStore,
} from '@/features/venue/venueService';
import {
  RemarkInfoItem,
  VenueRemarkModel,
  useVenueRemarkStore,
} from '@/features/venueRemarkInfo';
import api from '@/services/api/v2';
import { venueKey } from '@/services/queryKey';
import { ListQueryParamEnum } from '@/types/enums';
import VenueRemarkFieldInfo from '@/types/venueRemarkFieldInfo';

interface ActiveVenueQueryParams {
  venueId: number;
  isActive: boolean;
}

interface VenuesQueryParams
  extends Partial<Record<ListQueryParamEnum, string | number>> {
  clientId?: string | number | null;
}

export const venueService = {
  /**
   *  지점 비활성화/활성화
   */
  async activeVenue(payload: ActiveVenueQueryParams) {
    const { venueId, isActive } = payload;

    return await api.patch<PeriodicWorkTemplate.Detail>(
      `/venues/${venueId}/is-active`,
      {
        isActive,
      }
    );
  },
  /**
   * 입력 받는 리스팅 파라미터 및 고객사 ID에 따른 지점 리스트를 요청합니다.
   */
  async fetchVenueList({ clientId, search, ...rest }: VenuesQueryParams) {
    try {
      const { data } = await api.get<{ content: Venue.Item[] } & Pagination>(
        '/venues',
        {
          params: {
            clientId,
            q: search,
            ...rest,
          },
        }
      );
      return data;
    } catch (error) {
      throw new V2Error(error);
    }
  },

  async fetchVenueBrief(venueId: number) {
    const { data } = await api.get<Venue.Brief>(`/venues/${venueId}/simple`);
    return data;
  },

  async fetchVenueDetail(venueId: number) {
    try {
      const res = await api.get<Venue.Detail>(`/venues/${venueId}`);

      const data = cloneDeep(res.data);

      // 층을 미리 정렬해서 반환 (백엔드 작업 예정)
      data.floors = sortBy(data.floors, ['id']);

      return data;
    } catch (error) {
      throw new V2Error(error);
    }
  },

  async fetchVenueRemarkFieldInfo() {
    try {
      const venueStore = useVenueRemarkStore();

      const { data } = await api.get<VenueRemarkFieldInfo>(`/common/fields`);
      venueStore.venueRemarkFieldInfo = data as unknown as RemarkInfoItem[];
      return data;
    } catch (error) {
      throw new V2Error(error);
    }
  },

  async fetchVenueRemark(venueId: number) {
    try {
      const { data } = await api.get<Venue.Remarks>(
        `/venues/${venueId}/remarks`
      );
      return data;
    } catch (error) {
      throw new V2Error(error);
    }
  },

  async createVenue(payload: Venue.Payload.Create) {
    return await api.post('/venues', payload);
  },
  async updateVenue(venueId: number, payload: Venue.Payload.Update) {
    return await api.put(`/venues/${venueId}`, payload);
  },
  async updateVenueRemarks(venueId: number, payload: Venue.Remarks) {
    return await api.put(`venues/${venueId}/remarks`, payload);
  },
  async updateService(venueId: number, payload: Venue.ServicePayload) {
    return await api.put(`venues/${venueId}/services/used`, payload);
  },
  async fetchInvoices(venueId: number, year: number) {
    try {
      const { data } = await api.get(`venues/${venueId}/invoices?year=${year}`);
      return data;
    } catch (error) {
      throw new V2Error(error);
    }
  },
  async uploadInvoices(venueId: number, payload: Venue.InvoicePayload) {
    return await api.post(`venues/${venueId}/invoices`, payload);
  },
  async updateInvoices(
    venueId: number,
    invoiceId: number,
    payload: Venue.InvoicePayload
  ) {
    return await api.put(`venues/${venueId}/invoices/${invoiceId}`, payload);
  },
  async deleteInvoice(venueId: number, invoiceId: number) {
    return await api.delete(`venues/${venueId}/invoices/${invoiceId}`);
  },
  async patchGroupTitle(venueId: number, groupId: number, title: string) {
    return await api.patch(`/venues/${venueId}/groups/${groupId}`, { title });
  },
};

export function useAsyncActiveVenue(onSuccess, onError) {
  const { mutate } = useMutation<
    AxiosResponse,
    V2ErrorType,
    ActiveVenueQueryParams
  >((payload) => venueService.activeVenue(payload), {
    onSuccess: (res) => {
      onSuccess && onSuccess(res.data);
    },
    onError: (error) => {
      onError && onError(error);
    },
  });

  return mutate;
}

export function useFetchVenueList(params: VenuesQueryParams = {}) {
  const { data, isFetching, dataUpdatedAt } = useQuery(
    venueKey.listByPage({ ...params }),
    () => venueService.fetchVenueList(params),
    {
      enabled: !!params?.clientId,
      staleTime: milliseconds({ minutes: 1 }),
      suspense: false,
    }
  );

  return {
    isFetching,
    source: data,
    sourceUpdatedAt: dataUpdatedAt,
  };
}

export function useFetchVenueDetail(venueId: number, staleTimeInSeconds = 30) {
  const { isFetching, data, dataUpdatedAt, refetch } = useQuery<
    Venue.Detail,
    V2Error
  >(
    venueKey.detailById(venueId),
    () => venueService.fetchVenueDetail(venueId),
    {
      enabled: !!venueId && !Number.isNaN(venueId),
      staleTime: milliseconds({ seconds: staleTimeInSeconds }),
    }
  );

  return {
    isFetching,
    source: data,
    sourceUpdatedAt: dataUpdatedAt,
    refetch,
  };
}

export function useFetchVenueRemarkFieldInfo() {
  const { isFetching, data, dataUpdatedAt, refetch } = useQuery(
    venueKey.remarksFieldInfo(),
    () => venueService.fetchVenueRemarkFieldInfo(),
    {
      staleTime: Infinity,
      suspense: true,
    }
  );

  return {
    isFetching,
    data,
    dataUpdatedAt,
    refetch,
  };
}

export function useFetchVenueRemark(
  venueId: number,
  staleTimeInSeconds = 1000 * 60 * 1
) {
  const venueStore = useVenueRemarkStore();
  const { isFetching, data, dataUpdatedAt, refetch } = useQuery<
    Venue.Remarks,
    V2Error
  >(
    venueKey.remarksById(venueId),
    () => venueService.fetchVenueRemark(venueId),
    {
      enabled: !!venueId && !Number.isNaN(venueId),
      staleTime: milliseconds({ seconds: staleTimeInSeconds }),
      suspense: true,
      onSuccess: function (res) {
        venueStore.venueRemark = new VenueRemarkModel(res);
      },
    }
  );

  return {
    isFetching,
    source: data,
    sourceUpdatedAt: dataUpdatedAt,
    refetch,
  };
}

export function useFetchVenueBrief(venueId: number) {
  const { isLoading, isFetching, data, dataUpdatedAt, isError, error } =
    useQuery<Venue.Brief, V2Error>(
      venueKey.briefById(venueId),
      () => venueService.fetchVenueBrief(venueId),
      {
        enabled: !!venueId,
        staleTime: milliseconds({ minutes: 1 }),
        retry: false,
        suspense: false,
        useErrorBoundary: false,
      }
    );

  return {
    isLoading: isLoading || isFetching,
    isError,
    error,
    data,
    sourcesUpdatedAt: dataUpdatedAt,
  };
}

export function useAsyncCreateVenue(onSuccess, onError) {
  const queryClient = useQueryClient();
  const { mutate } = useMutation(
    (payload: Venue.Payload.Create) => venueService.createVenue(payload),
    {
      onSuccess(res) {
        queryClient.invalidateQueries([]); // TODO: 리프레시 필요한 인스턴스 리스트 뽑아 해당 인스턴스만 갱신하기
        onSuccess && onSuccess(res.data);
      },
      onError(error: V2ErrorType) {
        onError && onError(error);
      },
    }
  );

  return mutate;
}

export function useAsyncUpdateVenue(venueId: number, onSuccess, onError) {
  const queryClient = useQueryClient();

  const { mutate } = useMutation(
    (payload: Venue.Payload.Update) =>
      venueService.updateVenue(venueId, payload),
    {
      onSuccess(res) {
        queryClient.invalidateQueries(venueKey.domain()); // 지점 도메인 관련 전체 쿼리 인스턴스 캐시 무효화
        onSuccess && onSuccess(res.data);
      },
      onError(error: V2ErrorType) {
        onError && onError(error);
      },
    }
  );

  return mutate;
}

export function useUpdateVenueRemark(venueId: number, onSuccess, onError) {
  const { mutate, isLoading } = useMutation(
    (payload: Venue.Remarks) =>
      venueService.updateVenueRemarks(venueId, payload),
    {
      onSuccess(res) {
        onSuccess && typeof onSuccess === 'function' && onSuccess(res.data);
      },
      onError(error: V2ErrorType) {
        onError && typeof onError === 'function' && onError(error);
      },
    }
  );

  return { mutate, isLoading };
}

export function useFetchInvoices(venueId: number, year: number) {
  const { isFetching, data } = useQuery(
    venueKey.invoiceByListParams({ venueId, year }),
    () => venueService.fetchInvoices(venueId, year),
    {
      enabled: !!venueId && !Number.isNaN(venueId),
      suspense: true,
      staleTime: 1000 * 1 * 1,
    }
  );

  return {
    isFetching,
    data,
  };
}

export function useUploadInvoices(venueId: number, onSuccess, onError) {
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(
    (payload: Venue.InvoicePayload) =>
      venueService.uploadInvoices(venueId, payload),
    {
      onSuccess(res) {
        queryClient.invalidateQueries(
          venueKey.invoiceByListParams({ venueId })
        );
        onSuccess && typeof onSuccess === 'function' && onSuccess(res.data);
      },
      onError(error: V2ErrorType) {
        onError && typeof onError === 'function' && onError(error);
      },
    }
  );

  return { mutate, isLoading };
}

export function useUpdateInvoices(
  venueId: number,
  invoiceId: number,
  onSuccess,
  onError
) {
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(
    (payload: Venue.InvoicePayload) =>
      venueService.updateInvoices(venueId, invoiceId, payload),
    {
      onSuccess(res) {
        queryClient.invalidateQueries(
          venueKey.invoiceByListParams({ venueId })
        );
        onSuccess && typeof onSuccess === 'function' && onSuccess(res.data);
      },
      onError(error: V2ErrorType) {
        onError && typeof onError === 'function' && onError(error);
      },
    }
  );

  return { mutate, isLoading };
}

export function useDeleteInvoice(
  venueId: number,
  invoiceId: number,
  onSuccess?: (data: unknown) => void,
  onError?: (error: V2ErrorType) => void
) {
  const queryClient = useQueryClient();
  const { mutate } = useMutation(
    () => venueService.deleteInvoice(venueId, invoiceId),
    {
      onSuccess: function (res) {
        queryClient.invalidateQueries(
          venueKey.invoiceByListParams({ venueId })
        );
        onSuccess && typeof onSuccess === 'function' && onSuccess(res.data);
      },
      onError,
    }
  );

  return {
    mutate,
  };
}

export type UseFetchVenueServiceParams = {
  vid: number;
};

export function useFetchVenueService(params: UseFetchVenueServiceParams) {
  const { vid } = params;
  const store = useVenueServiceStore();
  return useQuery<ServicesDto>({
    queryKey: venueKey.servicesById(vid),
    async queryFn() {
      try {
        const { data } = await api.get(`venues/${vid}/services`);
        if (data && data.venueId === vid) {
          const serviceList = data.services.map(
            (innerData: ServiceWrapperDto) => {
              const obj: ServiceDto = innerData.service;
              return new VenueServiceModel({
                ...obj,
                ...{ used: innerData.used },
              });
            }
          );
          store.setServices(serviceList);
        }
        return data;
      } catch (error) {
        console.log(error);
      }
    },
    staleTime: 1000 * 60 * 60,
  });
}

export type UseUpdateServiceParams = {
  vid: number;
  onSuccess: (res) => void;
  onError: (err: V2ErrorType) => void;
};

export function useUpdateService(params: UseUpdateServiceParams) {
  const { vid, onSuccess, onError } = params;
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(
    (payload: Venue.ServicePayload) => venueService.updateService(vid, payload),
    {
      onSuccess(res) {
        queryClient.invalidateQueries(venueKey.servicesById(vid));
        onSuccess && typeof onSuccess === 'function' && onSuccess(res.data);
      },
      onError(error: V2ErrorType) {
        onError && typeof onError === 'function' && onError(error);
      },
    }
  );

  return { mutate, isLoading };
}

export type UseUpdateTitleParams = {
  venueId: number;
  groupId: number;
  title: string;
  onSuccess?: (data: unknown) => void;
  onError?: (error: V2ErrorType) => void;
};

export function useUpdateTitle(params: UseUpdateTitleParams) {
  const { venueId, groupId, title, onSuccess, onError } = params;

  const { mutate, isLoading } = useMutation(
    () => venueService.patchGroupTitle(venueId, groupId, title),
    {
      onSuccess(res) {
        onSuccess && typeof onSuccess === 'function' && onSuccess(res.data);
      },
      onError(error: V2ErrorType) {
        onError && typeof onError === 'function' && onError(error);
      },
    }
  );
  return { mutate, isLoading };
}

// export function useFetchVenueInfiniteList(parmas){}
